import type { ReactNode } from 'react'
import { useTranslate } from '@tolgee/react'
import { useSite } from './SiteProvider'
import { LocalePicker } from './LocalePicker'
import { clearConsent } from '../lib/consent'

/**
 * The chrome every screen shares: whose site this is, and how to get help.
 *
 * Shared between the funnel and the login screens on purpose. Someone arriving
 * at a password reset from an email needs to recognise the brand they are
 * handing a new password to, and a login page that looks nothing like the site
 * that sent them there is indistinguishable from a phishing page.
 */
export function PageShell({ children }: { children: ReactNode }) {
  const site = useSite()
  const { t } = useTranslate()

  return (
    <div className="min-h-dvh bg-canvas">
      <main className="mx-auto flex min-h-dvh max-w-md flex-col gap-6 p-6">
        <header className="flex items-center justify-between gap-3">
          <img src={site.logoUrl} alt={site.name} className="h-8 w-auto" />
          <LocalePicker />
        </header>

        {children}

        <footer className="mt-auto pt-6 text-xs text-muted">
          <p>
            {t('common.needHelp')}{' '}
            <a className="underline" href={`mailto:${site.supportEmail}`}>
              {site.supportEmail}
            </a>
          </p>
          <p className="mt-2">
            <a
              className="underline"
              href={site.legal.cookiePolicyUrl}
              target="_blank"
              rel="noreferrer"
            >
              {t('shell.cookiePolicy')}
            </a>
            {' · '}
            <a className="underline" href={site.legal.privacyUrl} target="_blank" rel="noreferrer">
              {t('shell.privacyPolicy')}
            </a>
            {' · '}
            {site.legal.entityName}
          </p>
          <p className="mt-1">
            <button
              type="button"
              className="underline"
              onClick={() => {
                clearConsent()
                window.location.reload()
              }}
            >
              {t('shell.cookiePreferences')}
            </button>
          </p>
          {site.legal.disclaimer && <Disclaimer text={site.legal.disclaimer} />}
        </footer>
      </main>
    </div>
  )
}

/**
 * The admin-authored footer disclaimer. A blank line separates paragraphs, and
 * a `**bold**` span becomes a `<strong>` — built as React nodes, never set as
 * HTML, so nothing an editor types can inject markup into the page.
 */
function Disclaimer({ text }: { text: string }) {
  return (
    <div className="mt-4 flex flex-col gap-2 leading-relaxed">
      {text.split(/\n{2,}/).map((para, i) => (
        <p key={i}>{renderEmphasis(para)}</p>
      ))}
    </div>
  )
}

function renderEmphasis(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) =>
    chunk.startsWith('**') && chunk.endsWith('**') ? (
      <strong key={i} className="font-semibold text-ink">
        {chunk.slice(2, -2)}
      </strong>
    ) : (
      <span key={i}>{chunk}</span>
    ),
  )
}
